/**
 * Тексты главной таблицы и правки по месту (§4, §5, §11.1–11.2 спеки).
 *
 * Общий `web/src/texts.ts` читают другие волны, поэтому свои строки держим
 * здесь и берём оттуда только служебные функции склонения.
 */
import { countOf, forms, plural } from '../../texts.js'

export const tableTexts = {
  /** §4.2 Заголовки колонок. Дословно из таблицы набора по умолчанию. */
  column: {
    gutter: 'Выделение и раскрытие',
    brand: 'Название',
    presence: 'Каналы',
    presenceFull: 'Присутствие в каналах',
    visibility: 'Видимость',
    visibilityFull: 'Видимость на сайте',
    tier: 'Тир',
    tierFull: 'Приоритет (тир)',
    url: 'URL',
    stockDisplay: 'Статус наличия',
    oos: 'Нет в наличии',
    seoPriority: 'Приоритет SEO',
    contextPriority: 'Приоритет Контекст',
    delivery: 'Доставка',
    installation: 'Установка',
    warranty: 'Гарантия',
    note: 'Примечание',
    missing: 'Пропущено',
    categoriesCount: 'Категории',
    categoriesCountFull: 'Категорий у бренда',
    comments: 'Комментарии',
    lastComment: 'Крайний комментарий/задача',
    unclear: 'Категории не уточнены',
  },

  /** §4.2 Группы колонок. */
  group: {
    base: 'Бренд',
    talk: 'Комментарии',
    site: 'Ссылка',
    priorities: 'Приоритет',
    wg: 'Уровень сервиса (White Glove)',
    wgShort: 'Сервис',
    seo: 'SEO',
    context: 'Контекст',
    other: 'Прочее',
    fields: 'Поля',
    collapse: (name: string) => `Свернуть группу ${name}`,
    expand: (name: string) => `Развернуть группу ${name}`,
    /* Видимая подпись короткая: «Свернуть группу Уровень сервиса (White Glove)»
       занимала две строки рядом с таким же двухстрочным заголовком. Полное имя
       остаётся для скринридера. */
    collapseShort: 'Свернуть',
    expandShort: 'Развернуть',
  },

  /** §12.3 Подсказки и объяснения механики — дословно. */
  hint: {
    wg: 'White Glove: доставка, установка, гарантия. Все три поля пока пусты у всех брендов.',
    myColumn: 'Ваше поле: за него отвечаете вы',
    missingColumn: 'Пустые поля из вашей зоны ответственности',
    naCell: 'Это поле не относится к категории',
    notInChannel: (brand: string, channel: string) => `${brand} не ведётся в ${channel}`,
    newOutsideFilter: 'Новый бренд показан вне фильтра, пока вы его не подтвердили',
    willLeaveFilter: 'После обновления эта строка уйдёт из текущего фильтра',
    othersDraft: (name: string) => `Этот бренд сейчас правит ${name}`,
    visibilityNo: 'Не показывается на сайте',
    expandAll: (brands: number, rows: number) =>
      `Развернуть ${countOf(brands, forms.brand)} — это ${countOf(rows, forms.row)}. Продолжить?`,
    hiddenByWidth: 'скрыта: не хватает ширины окна',
    wasValue: (previous: string, time: string) => `было: ${previous} · ${time} · видите только вы`,
    wasValueNoTime: (previous: string) => `было: ${previous} · видите только вы`,
    columnOfChannel: (column: string, channel: string) => `${column} (${channel.toUpperCase()})`,
    channelLetterPresent: (channel: string) => `Перейти в ${channel}`,
    channelLetterAbsent: (channel: string) => `Добавить в ${channel}`,
    foldedGroup: (filled: number, total: number, name: string) =>
      `${name}: заполнено ${filled} из ${total}. Клик разворачивает группу.`,
    categoryCount: (n: number) => `${countOf(n, forms.category)} · клик раскрывает`,
    commentsCount: (n: number) => `${n} · открыть комментарии`,
    noComments: 'Комментариев пока нет',
  },

  /** §5.4 Снимок строк. */
  snapshot: {
    drift: (n: number) => `${countOf(n, forms.row)} больше не подходят под фильтр`,
    refresh: 'Обновить',
    driftLabel: (n: number) => `${countOf(n, forms.row)} больше не подходят под фильтр. Обновить снимок`,
  },

  /** §4.5 Счётчики. */
  counter: {
    shown: 'Показано',
    of: 'из',
    universe: (n: number) => `${countOf(n, forms.brand)}`,
    copyLink: 'Ссылка на этот вид',
    linkCopied: 'Ссылка скопирована',
    linkFailed: 'Не удалось скопировать ссылку',
  },

  /** §4.3 Строки и состояния. */
  row: {
    expand: (name: string) => `Раскрыть категории бренда ${name}`,
    collapse: (name: string) => `Свернуть категории бренда ${name}`,
    select: (name: string) => `Выделить бренд ${name}`,
    selectAllShown: (n: number) => `Выделить все ${n} показанных`,
    archived: 'архив',
    isNew: 'новый',
    isNewLabel: 'бренд создан в вашем черновике',
    hasHiddenDraft: 'Внутри свёрнутых категорий есть ваша правка',
    noCategories: 'категории не заданы',
    addCategory: 'Добавить',
    unclear: 'Категории не уточнены',
    expandAllLabel: 'Развернуть все',
  },

  /** §5.2 Пустые значения кликабельны (П3). */
  empty: {
    url: 'добавить URL',
    note: 'добавить примечание',
    value: 'задать',
    tier: 'тир не задан',
    dash: '—',
  },

  /** §5.7 / §12.4 Массовые операции — дословно. */
  bulk: {
    note: 'Комментарий',
    noteSubmit: 'Добавить всем',
    noteHint: (count: number) => `Запишется ${count} брендам`,
    commentDone: (count: number) => `Комментарий добавлен ${count} брендам`,
    taskDone: (count: number) => `Задача поставлена ${count} брендам`,
    selected: (n: number) => `Выбрано ${countOf(n, forms.brand)}`,
    clear: 'Снять выделение',
    label: 'Массовые операции',
    pick: (column: string) => `${column} ▾`,
    target: (column: string, value: string) => `${column} → ${value}`,
    appliesTo: (brands: number, filled: number) =>
      `Применится к ${countOf(brands, forms.brand)}. У ${filled} из них поле уже заполнено.`,
    appliesToClean: (brands: number) => `Применится к ${countOf(brands, forms.brand)}. Поле пусто у всех.`,
    onlyEmpty: (n: number) => `Только пустые (${n})`,
    overwriteAll: (n: number) => `Перезаписать все (${n})`,
    bigPack: (n: number) => `Это ${countOf(n, forms.change)}. Они уйдут одним пакетом и одним сообщением в чат.`,
    cancel: 'Отмена',
    nothingToFill: 'Пустых значений в выборе нет',
    textPlaceholder: 'Значение для всех выбранных',
    textHint: 'Пустая строка очистит значение. На следующем шаге можно будет выбрать: заполнить только пустые или перезаписать всё.',
    textApply: 'Дальше',
    done: (column: string, n: number) => `${column} → проставлено у ${countOf(n, forms.brand)}`,
  },

  /** §4.2 Панель выбора колонок. */
  /** Список брендов на телефоне. */
  card: {
    seo: 'SEO',
    context: 'Контекст',
    categories: (n: number) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'категория' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'категории' : 'категорий'}`,
  },

  /** Шестерёнка в заголовке: настройки колонки и блока. */
  /* Комментарий в строке таблицы: ячейка и окно обсуждения. */
  comment: {
    empty: 'комментарий',
    now: 'сейчас',
    fromSheet: 'из таблицы',
    none: 'Пока ничего не написано',
    placeholder: 'Написать комментарий…',
    taskPlaceholder: 'Что нужно сделать?',
    send: 'Отправить',
    openCard: 'Открыть бренд →',
    failed: 'Не удалось добавить комментарий',
    addFor: (brand: string) => `Написать комментарий о бренде ${brand}`,
    openFor: (brand: string) => `Обсуждение бренда ${brand}`,
    /* Задачи */
    kindLabel: 'Что добавляем',
    kindComment: 'Комментарий',
    kindTask: 'Задача',
    dueLabel: 'Срок',
    due: (date: string) => `до ${date}`,
    markDone: 'Отметить выполненной',
    closeTask: 'Задача выполнена',
    cancel: 'Отмена',
    reportPlaceholder: 'Что сделано? Это увидят все',
    doneBy: (who: string) => `Выполнено · ${who}`,
    donePending: 'Выполнено · ждёт подтверждения',
    hasTask: 'У бренда есть незакрытая задача',
    toTask: 'Сделать задачей',
    toComment: 'Сделать комментарием',
    more: 'Ещё',
    reply: 'Ответить',
    replyCancel: 'Не отвечать',
    openTask: (body: string) => `Открытая задача: ${body}`,
  },

  columnSettings: {
    title: 'Настройки колонки',
    name: 'Название колонки',
    save: 'Сохранить',
    group: 'Блок таблицы',
    moved: (group: string) => `Колонка переехала в «${group}»`,
    enumLink: 'Значения справочника →',
    remove: 'Удалить колонку',
    removeTitle: (column: string) => `Удалить колонку «${column}»?`,
    removeText: 'Колонка исчезнет из таблицы. Уже записанные значения останутся в истории.',
    removeNote: 'Удаляются только заведённые колонки. Встроенные можно скрыть.',
    removeConfirm: 'Да, удалить',
    removed: (column: string) => `Колонка «${column}» удалена`,
    renamed: (title: string) => `Теперь это «${title}»`,
    groupTitle: 'Настройки блока',
    groupName: 'Название блока',
    groupRenamed: (title: string) => `Блок переименован в «${title}»`,
    addColumnHere: '+ Колонка в этот блок',
    failed: 'Не удалось сохранить',
    openColumn: (column: string) => `Настройки колонки «${column}»`,
    openGroup: (group: string) => `Настройки блока «${group}»`,
  },

  /* Удаление колонки и блока: два шага. Первый объясняет последствия,
     второй требует написать название — случайно такое не нажимается. */
  removeColumn: {
    columnTitle: (column: string) => `Удалить колонку «${column}»?`,
    groupTitle: (group: string) => `Удалить блок «${group}»?`,
    columnAction: 'Удалить колонку',
    groupAction: 'Удалить блок',
    whatColumn: 'Колонка исчезнет из таблицы, из карточек брендов и из выгрузки.',
    whatGroup: (count: number) => `Блок исчезнет из таблицы вместе со всеми колонками внутри — их ${count}:`,
    kept: 'Уже записанные значения остаются в истории: восстановить колонку сможет администратор, но в таблице её не будет ни у кого.',
    cannotUndo: 'Отменить это «Шагом назад» нельзя.',
    next: 'Понятно, дальше',
    confirmHint: (title: string) => `Чтобы подтвердить, напишите «${title}»`,
    confirmPlaceholder: 'Название',
    mismatch: 'Название не совпадает',
    finalColumn: 'Удалить колонку навсегда',
    finalGroup: 'Удалить блок навсегда',
    cancel: 'Отмена',
    doneColumn: (column: string) => `Колонка «${column}» удалена`,
    doneGroup: (group: string) => `Блок «${group}» удалён`,
    failed: 'Не удалось удалить',
    builtIn: 'Встроенные колонки не удаляются — их можно только скрыть.',
    deleteColumn: (column: string) => `Удалить колонку «${column}»`,
    deleteGroup: (group: string) => `Удалить блок «${group}»`,
  },

  columnsMenu: {
    title: 'Колонки',
    reset: 'Набор по умолчанию',
    pinLeft: 'Закрепить слева',
    unpin: 'Снять закрепление',
    pinnedOne: 'Слева закрепляется одна колонка — новая заменит прежнюю',
    required: 'нельзя отключить',
    hide: 'Скрыть колонку',
    columnMenu: (column: string) => `Меню колонки ${column}`,
    manageEnum: 'Настроить значения справочника →',
    groupsTitle: 'Группы',
    fieldsEmpty: 'Пользовательских полей пока нет',
    hiddenByWidth: 'скрыта: не хватает ширины окна',
    groupsHint: 'Порядок групп — перетаскиванием',
    moveGroup: (group: string) => `Переставить группу «${group}»: тащите мышью или Alt+стрелки`,
    moveColumn: (column: string) => `Переставить колонку «${column}»: тащите мышью или Alt+стрелки`,
    addColumn: 'Новый столбец',
    addLabel: 'Название нового столбца',
    addPlaceholder: 'Например, «Срок поставки»',
    addHint: 'Текстовый столбец. У розницы и опта значения свои.',
    addSubmit: 'Создать',
    addCancel: 'Отмена',
    addType: 'Тип значения',
    addEnum: 'Справочник значений',
    addEnumEmpty: '— выберите справочник —',
    addEnumLink: 'Завести новый справочник →',
    addNeedEnum: 'Для списка нужен справочник значений',
    addGroup: 'Блок таблицы',
    addGroupNew: '+ Новый блок',
    addGroupName: 'Название блока',
    addGroupPlaceholder: 'Например: Логистика',
    addNeedGroup: 'Назовите новый блок',
    addScope: 'Значение',
    addScopeHint: 'У значения по каналам Розница и Опт свои; общее — одно на бренд.',
    scopeChannel: 'Своё в каждом канале',
    scopeBrand: 'Общее для бренда',
    addFailed: 'Не удалось создать столбец',
  },

  /** §5.3 Правка. */
  edit: {
    cellLabel: (column: string, brand: string) => `${column} · ${brand}`,
    openEditor: 'Открыть редактор',
    nextEmpty: 'Следующая пустая ячейка в колонке',
    prevEmpty: 'Предыдущая пустая ячейка в колонке',
    noMoreEmpty: 'Пустых ячеек в этой колонке больше нет',
    missingInColumn: (column: string, total: number, left: number) =>
      `Пропусков в колонке ${column}: ${total} · осталось ${left}`,
    gridLabel: 'Таблица брендов',
    conflictCell: (author: string, current: string, mine: string) =>
      `${author} поставил ${current}. Ваша правка — ${mine}. Решится при подтверждении.`,
  },

  /** §11.4 Отказы по правам — §12.7 дословно. */
  denied: {
    contributorFilled:
      'Ваша роль позволяет только добавлять новое. Изменить заполненное значение нельзя — напишите комментарий, его увидят все',
    viewer: 'Ваша роль позволяет только смотреть данные',
    notInChannel: (brand: string, channel: string) => `${brand} не ведётся в ${channel}. Добавьте бренд в канал`,
  },

  /** §12.8 / §4.7 Пустые состояния — дословно. */
  state: {
    filtersZeroTitle: 'Под эти фильтры ничего не попало',
    filtersZeroText: (total: number, channel: string) =>
      `В канале ${channel} ${countOf(total, forms.brand)}. Снимите часть условий.`,
    searchZeroTitle: 'Среди показанных ничего не нашлось',
    searchZeroText: (total: number, channel: string) =>
      `Искать среди всех ${total} брендов канала ${channel}`,
    searchZeroAction: 'Сбросить поиск',
    channelEmptyTitle: 'В этом канале пока нет брендов',
    channelEmptyAction: 'Добавить бренд',
    clearAll: 'Очистить всё',
  },

  /** §11.1 Загрузка. */
  loading: {
    label: 'Загружаем бренды',
  },

  /** Служебное для склонений в своих строках. */
  plural,
} as const
